/**
 * Patient Context Card Component
 * 
 * Displays quick patient information and alerts in a compact card format.
 * Used in HOS sidebar and command palette.
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  AlertTriangle, 
  Calendar, 
  Clock, 
  Activity,
  Heart,
  Users,
  ChevronRight,
  Stethoscope
} from "lucide-react";

export function PatientContextCard({ 
  patient, 
  context, 
  onViewDetails,
  onOpenOverlay,
  compact = false 
}) {
  if (!patient) return null;

  const { quick_stats = {}, alerts = [], available_actions = [] } = context || {};

  // Get status badge color
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case "active": return "bg-green-500";
      case "pending": return "bg-amber-500";
      case "discharged": return "bg-slate-500";
      case "deceased": return "bg-purple-500";
      default: return "bg-slate-500";
    }
  };

  // Get alert severity color
  const getAlertColor = (severity) => {
    switch(severity?.toLowerCase()) {
      case "high": 
      case "critical": return "bg-red-500/20 border-red-500/30 text-red-300";
      case "warning": 
      case "medium": return "bg-amber-500/20 border-amber-500/30 text-amber-300";
      default: return "bg-blue-500/20 border-blue-500/30 text-blue-300";
    }
  };

  if (compact) {
    return (
      <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700" data-testid="patient-context-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
              <span className="text-sm font-bold text-white">
                {patient.name?.split(" ").map(n => n[0]).join("") || "?"}
              </span>
            </div>
            <div>
              <p className="font-medium text-white">{patient.name}</p>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Badge className={`${getStatusColor(patient.status)} text-white text-xs px-1.5 py-0`}>
                  {patient.status || "Active"}
                </Badge>
                {quick_stats.loc && <span>LOC: {quick_stats.loc}</span>}
              </div>
            </div>
          </div>
          {alerts.length > 0 && (
            <div className="flex items-center gap-1">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <Badge className="bg-amber-500/20 text-amber-300 text-xs">{alerts.length}</Badge>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden" data-testid="patient-context-card">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <span className="text-lg font-bold text-white">
                {patient.name?.split(" ").map(n => n[0]).join("") || "?"}
              </span>
            </div>
            <div>
              <h3 className="font-semibold text-white">{patient.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`${getStatusColor(patient.status)} text-white`}>
                  {patient.status || "Active"}
                </Badge>
                {patient.mrn && (
                  <span className="text-xs text-slate-400">MRN: {patient.mrn}</span>
                )}
              </div>
            </div>
          </div>
          {onViewDetails && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onViewDetails}
              className="text-slate-400 hover:text-white"
            >
              View Details
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      {quick_stats && Object.keys(quick_stats).length > 0 && (
        <div className="px-4 py-3 grid grid-cols-3 gap-3 border-b border-slate-700/50">
          {quick_stats.days_in_program !== undefined && (
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-slate-400 mb-1">
                <Calendar className="w-3 h-3" />
                <span className="text-xs">Days</span>
              </div>
              <p className="text-lg font-bold text-white">{quick_stats.days_in_program}</p>
            </div>
          )}
          {quick_stats.benefit_period && (
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-slate-400 mb-1">
                <Activity className="w-3 h-3" />
                <span className="text-xs">Period</span>
              </div>
              <p className="text-lg font-bold text-white">{quick_stats.benefit_period}</p>
            </div>
          )}
          {quick_stats.loc && (
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 text-slate-400 mb-1">
                <Stethoscope className="w-3 h-3" />
                <span className="text-xs">LOC</span>
              </div>
              <p className="text-lg font-bold text-white">{quick_stats.loc}</p>
            </div>
          )}
        </div>
      )}

      {/* Visit Info */}
      {(quick_stats.last_visit || quick_stats.next_visit) && (
        <div className="px-4 py-2 grid grid-cols-2 gap-2 border-b border-slate-700/50 text-sm">
          {quick_stats.last_visit && (
            <div className="flex items-center gap-2 text-slate-400">
              <Clock className="w-3 h-3" />
              <span>Last: {quick_stats.last_visit}</span>
            </div>
          )}
          {quick_stats.next_visit && (
            <div className="flex items-center gap-2 text-teal-400">
              <Calendar className="w-3 h-3" />
              <span>Next: {quick_stats.next_visit}</span>
            </div>
          )}
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="p-3 space-y-2">
          <p className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Active Alerts
          </p>
          {alerts.slice(0, 3).map((alert, idx) => (
            <div 
              key={idx}
              className={`p-2 rounded-lg border text-sm ${getAlertColor(alert.severity)}`}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{alert.message}</span>
              </div>
            </div>
          ))}
          {alerts.length > 3 && (
            <p className="text-xs text-slate-500 text-center">
              +{alerts.length - 3} more alerts
            </p>
          )}
        </div>
      )}

      {/* Quick Actions */}
      {available_actions.length > 0 && onOpenOverlay && (
        <div className="p-3 border-t border-slate-700/50">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Quick Actions</p>
          <div className="flex flex-wrap gap-1.5">
            {available_actions.slice(0, 4).map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                onClick={() => onOpenOverlay(action.id)}
                className={`text-xs border-slate-600 text-slate-300 hover:bg-slate-700 ${
                  action.priority === "high" ? "border-amber-500/50 text-amber-300" : ""
                }`}
              >
                {action.name}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PatientContextCard;
